import { NewRequestClientsTable } from '../components/NewRequest/NewRequestClientsTable';
import { NewRequestBranchTable } from '../components/NewRequest/NewRequestBranchTable';
import { NewRequestBranchDevices } from '../components/NewRequest/NewRequestBranchDevices';
import { NewRequestInvoices } from '../components/NewRequest/NewRequestInvoices';
import React, { Suspense, useContext, useState } from 'react';
import { NewRequestContext } from '../context/NewRequestContext';
import { Alert, Button, Modal, Spinner } from 'react-bootstrap';
import { AuthContext } from '../context/AuthContext';
import { Role } from '../utils/Enums/Roles';
import { Invoices } from '../components/Dashboard/Invoices';
import { ClientBranches } from '../components/Dashboard/ClientBranches';
import { RentalAgreementProps } from '../models/warehouse-models';
import { getRentalAgreement } from '../utils/warehouses-hooks';
import { toast } from 'react-toastify';
import { CustomToastContent } from '../components/CustomToastContent';
import { toastOptionsTopRight } from '../utils/Enums/ToastOptions';
import { useTranslation } from 'react-i18next';
import { useGetBranchImage } from '../utils/dashboard-hooks';
import noImage from '../assets/No_image_available.png';
import { Protocols } from '../components/Dashboard/Protocols';
import { CustomStepWizardNav } from '../components/CustomStepWizardNav';
import StepWizard from 'react-step-wizard';
import { usePagination } from '../utils/pagination-hooks';
import { ClientInvoicesInner } from '../components/NewRequest/ClientInvoicesInner';
import { useLocation } from 'react-router-dom';
import { CashOrdersTable } from '../components/NewRequest/CashOrdersTable';
import { useFreshRequestBranch } from '../utils/useFreshRequestBranch';
import { useRequestInvoices } from '../utils/useRequestInvoices';

interface NewRequestProps {
    showModal: boolean;
    setShowModal: React.Dispatch<React.SetStateAction<boolean>>;
    setIsNewMarkerVissible?: React.Dispatch<React.SetStateAction<boolean>>;
    setShowNewRequestModal?: React.Dispatch<React.SetStateAction<boolean>>;
}

export const NewRequest = ({
    showModal,
    setShowModal,
    setIsNewMarkerVissible,
    setShowNewRequestModal,
}: NewRequestProps) => {
    const { t } = useTranslation('translation', { keyPrefix: 'newRequest' });
    const {
        selectedBranch,
        selectedBranchId,
        selectedClientId,
        updateBranchAfterSave,
        selectedClient,
        clearSelection,
        isLoadingSelection,
        selectionLoadFailed,
        retrySelection,
        resourcesLoadFailed,
        retryResources,
        showOnlyClient,
    } = useContext(NewRequestContext);
    const { hasPermission } = useContext(AuthContext);
    const isDistributor =
        !hasPermission([Role.COORDINATOR]) && (hasPermission([Role.DISTRIBUTOR]) || hasPermission([Role.INSTALLER]));
    const canViewCashOrders = hasPermission([Role.ADMIN, Role.FINANCIAL, Role.COORDINATOR]);
    const pagination = usePagination(100);
    const location = useLocation();
    const {
        paidInvoices, unpaidInvoices, paidInvoicesForClient, unpaidInvoicesForClient,
        failed: invoicesLoadFailed, loading: invoicesLoading, reload: reloadInvoices,
    } =
        useRequestInvoices(showOnlyClient ? undefined : selectedBranchId, selectedClientId, showModal,
            pagination.currentPage, pagination.pageSize);

    useFreshRequestBranch(selectedBranch.id, showModal, updateBranchAfterSave);

    const printDocument = (blob: Blob) => {
        if (blob) {
            const pdf = URL.createObjectURL(blob);
            window.open(pdf, '_blank');
        } else {
            toast.error(<CustomToastContent content={[`${t('printError')}`]} whiteText />, toastOptionsTopRight);
        }
    };

    const printRental = async () => {
        const props: RentalAgreementProps = {
            branchId: selectedBranch.id,
        };
        const blob = await getRentalAgreement(props);
        printDocument(blob);
    };

    return (
        <div>
            <Modal
                show={showModal}
                onHide={() => {
                    setShowModal(false);
                    clearSelection();
                    if (location.pathname === '/customers') {
                        localStorage.removeItem('markerID');
                        const storedPosition = JSON.parse(localStorage.getItem('scrollPosition') || '0');
                        window.scrollTo({ top: storedPosition, behavior: 'smooth' });
                    }
                }}
                size='lg'
                centered
                className={'branches-modal'}
            >
                <Modal.Header closeButton />
                <Modal.Body>
                    {!isLoadingSelection && invoicesLoadFailed && (
                        <LoadError messageKey='invoicesLoadError' onRetry={reloadInvoices} />
                    )}
                    {!isLoadingSelection && resourcesLoadFailed && (
                        <LoadError messageKey='requestDataLoadError' onRetry={retryResources} />
                    )}
                    {isLoadingSelection ? (
                        selectionLoadFailed
                            ? <LoadError messageKey='selectionLoadError' onRetry={retrySelection} />
                            : <Spinner animation='border' />
                    ) : showOnlyClient ? (
                        <div className='d-flex flex-column w-100'>
                            <div className='d-flex w-100'>
                                <div className='w-70'>
                                    <ClientBranches />
                                    <NewRequestClientsTable />
                                </div>
                                {/* min-w-0: иначе флекс елементът не пада под ширината на таблицата и тя
                                    излиза извън диалога, вместо да се скролва хоризонтално. */}
                                <div className='w-100 min-w-0'>
                                    <Suspense fallback={<Spinner animation='border'></Spinner>}>
                                        <StepWizard
                                            className='full-width-table d-flex flex-wrap w-100'
                                            nav={<CustomStepWizardNav steps={['unpaid', 'paid']} />}
                                            transitions={{
                                                enterRight: '',
                                                enterLeft: '',
                                                exitRight: '',
                                                exitLeft: '',
                                            }}
                                        >
                                            <ClientInvoicesInner
                                                invoicesData={unpaidInvoicesForClient}
                                                refetchEntitiesHook={reloadInvoices}
                                                pagination={pagination}
                                            />
                                            <ClientInvoicesInner
                                                invoicesData={paidInvoicesForClient}
                                                refetchEntitiesHook={reloadInvoices}
                                                pagination={pagination}
                                            />
                                        </StepWizard>
                                    </Suspense>
                                </div>
                            </div>
                            {selectedClient.id && canViewCashOrders && <CashOrdersTable clientId={selectedClient.id} />}
                        </div>
                    ) : selectedBranch.id ? (
                        <div className='d-flex w-100'>
                            <div className='new-request-tables request-branch-column'>
                                <Suspense fallback={<Spinner animation='border'></Spinner>}>
                                    <ClientBranches />
                                </Suspense>
                                <div className={'d-flex w-100 justify-content-center m-2'}>
                                    <Suspense fallback={<Spinner animation='border'></Spinner>}>
                                        <RenderImage id={selectedBranch.id} />
                                    </Suspense>
                                </div>
                                <Button className='w-100' onClick={printRental}>
                                    {t('rentalAgreement')}
                                </Button>
                                <NewRequestBranchTable />
                                <NewRequestClientsTable />
                            </div>
                            <div className='new-request-tables request-details-column'>
                                <div className='d-flex'>
                                    {selectedBranch.id && !isDistributor && (
                                        <NewRequestInvoices
                                            paidInvoices={paidInvoices}
                                            unpaidInvoices={unpaidInvoices}
                                        />
                                    )}
                                    <div className='d-flex flex-column'>
                                        {selectedBranch.id && (
                                            <Suspense fallback={<Spinner animation='border'></Spinner>}>
                                                <Invoices
                                                    fetchHook={reloadInvoices}
                                                    paidInvoices={paidInvoices}
                                                    unpaidInvoices={unpaidInvoices}
                                                    loading={invoicesLoading}
                                                />
                                            </Suspense>
                                        )}
                                        <Suspense fallback={<Spinner animation='border'></Spinner>}>
                                            {selectedBranch.id && (
                                                <Protocols
                                                    branchId={selectedBranch.id}
                                                    refetchEntitiesHook={reloadInvoices}
                                                />
                                            )}
                                        </Suspense>
                                        {selectedClient.id && canViewCashOrders && (
                                            <CashOrdersTable
                                                clientId={selectedClient.id}
                                                branchId={selectedBranch.id}
                                            />
                                        )}
                                    </div>
                                </div>
                                <NewRequestBranchDevices />
                            </div>
                        </div>
                    ) : (
                        <div className='d-flex'>
                            <Suspense fallback={<Spinner animation='border'></Spinner>}>
                                <NewRequestClientsTable />
                                {selectedClient.id && (
                                    <NewRequestBranchTable setIsNewMarkerVissible={setIsNewMarkerVissible} />
                                )}
                            </Suspense>
                        </div>
                    )}
                </Modal.Body>
                <Modal.Footer />
            </Modal>
        </div>
    );
};

const LoadError = ({ messageKey, onRetry }: { messageKey: string; onRetry: () => void }) => {
    const { t } = useTranslation('translation', { keyPrefix: 'common' });
    return (
        <Alert variant='danger' className='d-flex align-items-center justify-content-between'>
            <span>{t(messageKey)}</span>
            <Button variant='outline-danger' size='sm' onClick={onRetry}>
                {t('retryLoad')}
            </Button>
        </Alert>
    );
};

const RenderImage = ({ id }: { id: number }) => {
    const [getBranchImage] = useGetBranchImage(id);
    const image = getBranchImage();

    const [showImage, setShowImage] = useState(false);

    return (
        <div>
            <div className={'d-flex w-100 justify-content-center m-2'}>
                <div className='branch-image-container rounded'>
                    {image ? (
                        <img src={image} alt='branch' className='branch-image' onClick={() => setShowImage(true)} />
                    ) : (
                        <img src={noImage} className='branch-image not-available' alt='not available' />
                    )}
                </div>
            </div>
            <Modal
                show={showImage}
                onHide={() => setShowImage(false)}
                size='lg'
                centered
                className={'transparent-modal'}
            >
                <Modal.Body>
                    <div className='d-flex w-100 justify-content-center p-0 m-0'>
                        <img src={image} alt='branch' className='image' />
                    </div>
                </Modal.Body>
            </Modal>
        </div>
    );
};
